import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * Return value from `onSubmit`:
 * - `true`: submission accepted; dialog closes.
 * - `false`: submission rejected silently; dialog stays open.
 * - `string`: submission rejected with the given inline error; dialog stays open.
 *
 * Callers must return one of these values explicitly. `void` is intentionally
 * excluded — an unannotated handler would let an accidental no-return path
 * silently fall through to "accept" or "reject" depending on type widening;
 * being explicit avoids that footgun.
 */

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  label: string;
  placeholder?: string;
  initialValue?: string;
  submitLabel?: string;
  /**
   * Invoked with the trimmed value when the user submits a non-empty input.
   * Return `false` or a string to keep the dialog open (e.g. for duplicate
   * validation); the string is shown inline as an error message.
   */
  onSubmit: (value: string) => boolean | string;
}

/**
 * Single-input prompt dialog. Replaces `window.prompt()` so input flows match
 * the design system.
 */
export function PromptDialog({
  open,
  onOpenChange,
  title,
  description,
  label,
  placeholder,
  initialValue = '',
  submitLabel = 'OK',
  onSubmit,
}: Props): React.ReactElement {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValue(initialValue);
      setError(null);
    }
  }, [open, initialValue]);

  function handleSubmit(event: React.FormEvent): void {
    event.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    const result = onSubmit(trimmed);
    if (result === false) {
      // Silent rejection — keep dialog open, no inline error.
      return;
    }
    if (typeof result === 'string') {
      setError(result);
      return;
    }
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block space-y-1.5 text-sm text-text-secondary">
            <span>{label}</span>
            <Input
              autoFocus
              value={value}
              onChange={e => {
                setValue(e.target.value);
                if (error) setError(null);
              }}
              placeholder={placeholder}
              aria-invalid={error ? true : undefined}
            />
          </label>
          {error && (
            <p className="text-sm text-error" role="alert">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={(): void => {
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!value.trim()}>
              {submitLabel}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
